import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import type { AssessmentOverview } from '@assessments/shared'
import { useAssessmentOverview } from '../features/assessment-overview/useAssessmentOverview.js'

export interface RequireTermsAcceptedProps {
  organizationSurveyId: string
  /** A render function receives the overview the guard loaded, so callers needn't refetch it. */
  children: ReactNode | ((overview: AssessmentOverview) => ReactNode)
}

// Redirects to the accept screen when the assessment has an enabled T&C prerequisite
// the organization hasn't accepted yet. The overview's `prerequisites` are the single
// source of truth for gate state, so direct navigation is enforced the same way the
// overview surfaces the gate.
export function RequireTermsAccepted({
  organizationSurveyId,
  children,
}: RequireTermsAcceptedProps) {
  const { data, loading, error } = useAssessmentOverview(organizationSurveyId)

  // Until the overview loads we can't know the gate state; render nothing rather
  // than briefly exposing a gated section.
  if (loading) {
    return <main className="min-w-0 p-1 text-sm text-subtle">Loading…</main>
  }
  // A fetch failure must not strand the guard on a perpetual "Loading…" with no
  // feedback or retry — surface the error like the overview does.
  if (error || !data) {
    return (
      <main className="min-w-0 p-1 text-sm text-subtle">
        Couldn’t load the assessment. Please try again.
      </main>
    )
  }

  const termsGate = data.prerequisites?.find((p) => p.kind === 'terms_and_conditions')
  if (termsGate && !termsGate.complete) {
    return <Navigate to={`/assessments/${organizationSurveyId}/terms`} replace />
  }

  return <>{typeof children === 'function' ? children(data) : children}</>
}
